import { amplifyProxy } from '@/services/directApi'

const state = {
  serviceTypes: [],
  areas: [],
  searchResults: {
    landlords: [],
    tenants: [],
    properties: [],
    contractors: [],
    people: [],
    tenancies: []
  },
  enumOptions: {
    PaymentFrequency: [],
    TenancyType: [],
    RentPaymentMethod: []
  },
  currentTenancy: null,
  loading: false,
  error: null,
  propertyVerification: {
    loading: false,
    canSubmit: true,
    advisories: [],
    propertyId: null
  }
}

const getters = {
  serviceTypeOptions: state => {
    if (!Array.isArray(state.serviceTypes)) {
      return []
    }
    return state.serviceTypes.map(type => {
      if (!type) {
        return { value: '', text: 'Unknown' }
      }
      return {
        value: type.OID || type.oid || type.id || type.ID,
        text: type.Name || type.name || type.label || type.text || 'Unknown'
      }
    })
  },

  areaOptions: state => {
    if (!Array.isArray(state.areas)) {
      return []
    }
    return state.areas.map(area => ({
      value: area.OID,
      text: area.Name,
      branchOID: area.BranchOID
    }))
  },
  
  isLoading: state => state.loading,
  
  hasError: state => !!state.error,
  
  getSearchResultsByType: state => type => state.searchResults[type] || [],
  
  propertyVerification: state => state.propertyVerification,
  
  canSubmitTenancy: state => state.propertyVerification.canSubmit
}

const mutations = {
  SET_SERVICE_TYPES(state, types) {
    state.serviceTypes = types
  },
  
  SET_AREAS(state, areas) {
    state.areas = areas
  },
  
  SET_SEARCH_RESULTS(state, { type, results }) {
    state.searchResults[type] = results
  },
  
  CLEAR_SEARCH_RESULTS(state, type) {
    if (type) {
      state.searchResults[type] = []
    } else {
      state.searchResults = {
        landlords: [],
        tenants: [],
        properties: [],
        contractors: [],
        people: [],
        tenancies: []
      }
    }
  },
  
  SET_ENUM_OPTIONS(state, { enumName, options }) {
    state.enumOptions[enumName] = options
  },
  
  SET_CURRENT_TENANCY(state, tenancy) {
    state.currentTenancy = tenancy
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  CLEAR_ERROR(state) {
    state.error = null
  },
  
  SET_PROPERTY_VERIFICATION(state, { canSubmit, advisories, propertyId }) {
    state.propertyVerification.canSubmit = canSubmit
    state.propertyVerification.advisories = advisories || []
    state.propertyVerification.propertyId = propertyId
  },
  
  SET_PROPERTY_VERIFICATION_LOADING(state, loading) {
    state.propertyVerification.loading = loading
  },
  
  CLEAR_PROPERTY_VERIFICATION(state) {
    state.propertyVerification = {
      loading: false,
      canSubmit: true,
      advisories: [],
      propertyId: null
    }
  }
}

const actions = {
  async initializeFormData({ dispatch }) {
    try {
      await Promise.all([
        dispatch('fetchServiceTypes'),
        dispatch('fetchAreas'),
        dispatch('fetchEnumOptions', 'PaymentFrequency'),
        dispatch('fetchEnumOptions', 'TenancyType'),
        dispatch('fetchEnumOptions', 'RentPaymentMethod')
      ])
    } catch (error) {
      // Initialization errors are handled silently
    }
  },

  async fetchServiceTypes({ commit, rootState }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/tenancies/servicetypes`, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      
      // The response structure may vary, let's handle all cases
      let serviceTypes = []
      
      // First check if the response itself is an array
      if (Array.isArray(response)) {
        serviceTypes = response
      }
      // Then check for response.data (Lambda proxy pattern)
      else if (response && response.data) {
        if (Array.isArray(response.data)) {
          serviceTypes = response.data
        }
      }
      // Check for nested data.data structure
      else if (response && response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          serviceTypes = response.data.data
        }
      }
      
      commit('SET_SERVICE_TYPES', serviceTypes)
      return serviceTypes
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch service types')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAreas({ commit, rootState }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/company/areas/0/1000`, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      
      // Handle response structure similar to service types
      let areasData = []
      
      // First check if the response itself is an array
      if (Array.isArray(response)) {
        areasData = response
      }
      // Then check for response.data (Lambda proxy pattern)
      else if (response && response.data) {
        // Check if data is the areas array directly
        if (Array.isArray(response.data)) {
          areasData = response.data
        }
        // Check for paginated response with Data property
        else if (response.data.Data && Array.isArray(response.data.Data)) {
          areasData = response.data.Data
        }
        // Check for nested data structure
        else if (response.data.data) {
          if (Array.isArray(response.data.data)) {
            areasData = response.data.data
          } else if (response.data.data.Data && Array.isArray(response.data.data.Data)) {
            areasData = response.data.data.Data
          }
        }
      }
      
      commit('SET_AREAS', areasData)
      return areasData
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch areas')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // TODO: This searchEntity functionality should be moved to its own dedicated Vuex module
  // Currently residing in TenanciesManagement for time constraints, but it's used across
  // multiple features (tenancies, contractors, payment requests, reconciled statements)
  // Future refactor: Create a CloudSearch module that handles all entity searches
  async searchEntity({ commit, rootState }, { schemaType, searchTerm }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    if (!searchTerm || searchTerm.length < 5) {
      commit('SET_LOADING', false)
      return []
    }

    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/search/${schemaType}`, {
        headers: {
          token: rootState.user.LoginToken
        },
        queryStringParameters: {
          searchTerm: searchTerm
        }
      })
      
      let type
      switch (schemaType) {
        case 'LettingsLandlord':
          type = 'landlords'
          break
        case 'LettingsTenant':
          type = 'tenants'
          break
        case 'PropertyOwnable':
          type = 'properties'
          break
        case 'MaintenanceContractor':
          type = 'contractors'
          break
        case 'ApplicationPerson':
          type = 'people'
          break
        case 'LettingsTenancy':
          type = 'tenancies'
          break
        default:
          throw new Error(`Unknown schema type: ${schemaType}`)
      }
      
      // Extract the Data array from CloudSearch response
      // Response structure: { data: { Data: [...], Count: N } }
      const results = (response.data && response.data.Data) ? response.data.Data : []
      commit('SET_SEARCH_RESULTS', { type, results })

      return results
    } catch (error) {
      commit('SET_ERROR', error.message || 'Search failed')
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchEnumOptions({ commit, rootState }, enumName) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/company/enums/dropdown/values`, {
        headers: {
          token: rootState.user.LoginToken
        },
        queryStringParameters: {
          enumName: enumName
        }
      })
      const options = response.data || []
      commit('SET_ENUM_OPTIONS', { enumName, options: Array.isArray(options) ? options : [] })
      return options
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to fetch ${enumName} options`)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createTenancy({ commit, rootState }, tenancyData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await amplifyProxy.post(`/${rootState.user.ClientName}/tenancies`, tenancyData, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      
      // Handle the Lambda proxy response structure
      if (response && response.data) {
        return response.data
      }
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create tenancy')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTenancy({ commit, rootState }, tenancyId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/tenancies/${tenancyId}`, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      commit('SET_CURRENT_TENANCY', response)
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch tenancy')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTenancy({ commit, rootState }, { tenancyId, tenancyData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await amplifyProxy.patch(`/${rootState.user.ClientName}/tenancies/${tenancyId}`, tenancyData, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update tenancy')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async verifyProperty({ commit, rootState }, propertyId) {
    commit('SET_PROPERTY_VERIFICATION_LOADING', true)

    try {
      const response = await amplifyProxy.get(`/${rootState.user.ClientName}/tenancies/verify/${propertyId}`, {
        headers: {
          token: rootState.user.LoginToken
        }
      })
      
      // Handle response structure
      let verificationData = {}
      if (response && response.data) {
        verificationData = response.data
      } else if (response) {
        verificationData = response
      }
      
      commit('SET_PROPERTY_VERIFICATION', {
        canSubmit: verificationData.CanSubmit !== false, // Default to true if not explicitly false
        advisories: verificationData.Advisories || [],
        propertyId: propertyId
      })
      
      return verificationData
    } catch (error) {
      // If verification fails, default to allowing submission but log the error
      console.error('Property verification failed:', error)
      commit('SET_PROPERTY_VERIFICATION', {
        canSubmit: true,
        advisories: [],
        propertyId: propertyId
      })
      return { CanSubmit: true, Advisories: [] }
    } finally {
      commit('SET_PROPERTY_VERIFICATION_LOADING', false)
    }
  },
  
  clearPropertyVerification({ commit }) {
    commit('CLEAR_PROPERTY_VERIFICATION')
  },
  
  clearSearchResults({ commit }, type) {
    commit('CLEAR_SEARCH_RESULTS', type)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}